import {VALUES} from '@github-ui/timeline-items/Values'
// getFocusableChild only runs in a client-side callback after a load completes,
// so it never affects SSR.
// eslint-disable-next-line no-restricted-imports
import {getFocusableChild} from '@primer/behaviors/utils'

const wrapperSelector = (id: string) => `[${VALUES.timeline.dataWrapperTimelineEventId}="${id}"]`

// The "Load more"/"Load all" control. Its own subtree (including its
// "N remaining items" heading) must never be treated as a focus target.
export const LOAD_CONTROL_SELECTOR = '[data-testid^="issue-timeline-load-more"]'

// Selector for one specific load control. A top and a bottom control can coexist
// when deep-linked to a highlighted comment, so focus restore must wait only on
// the control that was activated rather than any control on the page.
export const loadControlSelector = (position: 'top' | 'bottom') =>
  `[data-testid="issue-timeline-load-more-load-${position}"]`

// Newly-loaded rows are inserted after the base item, sometimes separated by a
// non-focusable divider element, so we walk a few siblings before giving up.
const MAX_SIBLING_WALK = 10

const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"]'

function isLoadControl(el: HTMLElement): boolean {
  return el.matches(LOAD_CONTROL_SELECTOR) || el.querySelector(LOAD_CONTROL_SELECTOR) !== null
}

/**
 * Resolves a *meaningful* focus target inside a timeline row.
 *
 * A comment's first focusable child is its avatar link (`@user's profile`), which
 * tells a screen-reader user nothing about the content they just loaded. We instead
 * prefer the row's heading (e.g. "<author> commented"), making it programmatically
 * focusable so focus lands on the comment itself. Rows without a heading (grouped
 * events) fall back to their first focusable descendant.
 *
 * Returns `null` for the load control itself, so it is never focused.
 */
function resolveTargetWithin(row: HTMLElement): HTMLElement | null {
  if (isLoadControl(row)) return null

  const heading = row.querySelector<HTMLElement>(HEADING_SELECTOR)
  if (heading) {
    // Headings aren't focusable by default; -1 keeps it out of the tab order
    // while allowing programmatic focus.
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1')
    return heading
  }
  return getFocusableChild(row) ?? null
}

/**
 * Resolves the focus target for the first newly-loaded timeline row, anchored on
 * the item immediately *before* the "Load more"/"Load all" control (which gets
 * pushed down or unmounted once items load).
 *
 * Comments render as direct children; all other events are grouped inside a
 * `<section>`. In both cases the newly-loaded content is the next sibling after
 * the base item's wrapper (comment) or its `<section>` (grouped event). We skip
 * non-focusable separators (e.g. timeline dividers) and the load control itself.
 *
 * Returns `null` when the new content is not yet in the DOM (e.g. the load is
 * still in progress and the control is still adjacent), so the caller can wait.
 */
export function resolveTimelineFocusTarget(baseId: string, isComment: boolean): HTMLElement | null {
  const baseElement = document.querySelector<HTMLElement>(wrapperSelector(baseId))
  if (!baseElement) return null

  const anchor = isComment ? baseElement : (baseElement.closest('section') ?? baseElement)

  let sibling = anchor.nextElementSibling as HTMLElement | null
  let walked = 0
  while (sibling && walked < MAX_SIBLING_WALK) {
    const target = resolveTargetWithin(sibling)
    if (target) return target
    sibling = sibling.nextElementSibling as HTMLElement | null
    walked++
  }

  return null
}

/**
 * Stable fallback: the base item itself remains in the DOM after a load, so we
 * focus it (its heading where possible) when the newly-loaded content cannot be
 * resolved. This guarantees focus order stays logical and never
 * drops to `<body>`.
 */
export function resolveBaseItemFocusTarget(baseId: string): HTMLElement | null {
  const baseElement = document.querySelector<HTMLElement>(wrapperSelector(baseId))
  if (!baseElement) return null
  return resolveTargetWithin(baseElement)
}

export type TimelineFocusDecision = 'wait' | 'abort' | 'focus-content'

/**
 * Decides what the post-load focus effect should do after a timeline commit, given
 * the observable state. Kept pure so the focus matrix can be unit-tested directly;
 * the hook supplies the inputs from the DOM and carries out the result.
 *
 * After any load, focus moves to the newly-loaded content — that is the point of
 * activating "Load more"/"Load all", and it lets a screen-reader user read forward
 * through what they just loaded. The only nuance is timing:
 *
 * - `focusEscaped` — focus is on real content the user navigated to (not the
 *   control or `<body>`); never steal it back.
 * - **Load all** streams rows across several commits and only removes the control
 *   on the last, so wait until it's gone before focusing — otherwise a transient
 *   intermediate row (later remounted) would be targeted.
 * - **Partial "Load more"** lands its page in one commit (the timeline page remounts
 *   because its React key includes the row count), so focus the new rows as soon as
 *   they've grown — whether or not the control persists below them.
 */
export function decideTimelineFocus(input: {
  isLoadAll: boolean
  controlPresent: boolean
  itemsGrew: boolean
  focusEscaped: boolean
}): TimelineFocusDecision {
  if (input.focusEscaped) return 'abort'
  if (input.isLoadAll) return input.controlPresent ? 'wait' : 'focus-content'
  return input.itemsGrew ? 'focus-content' : 'wait'
}
